import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext,
} from "react";
import {
  Table,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
} from "reactstrap";
import {
  IRiderManagementListProps,
  IRiderManagementNormalized,
} from "../../../interfaces/riderManagement.interface";
import { _ } from "../../../utils/lib";
import Toaster from "../../common/Toaster/Toaster";
import LoaderComponent from "../../common/Loader/Loader";
import ButtonComponent from "../../common/Button/ButtonComponent";
import "./RiderManagementList.scss";
import searchicon from "../../../assets/images/search-icon.svg";
import close from "../../../assets/images/close.svg";
import {
  ACTIVATED_TAG,
  CHECK_NUMBER_REGEX,
  DEACTIVATED_TAG,
  STATUS,
} from "../../../config/constants";
import { Can } from "config/Can";

export const doAPIsearch = (
  val: any,
  pageScrollPosition: any,
  getRiderList: any
) => {
  if (val.length >= 3) {
    pageScrollPosition.current = 0;
    getRiderList({
      searchKey: val,
    });
  }
};

const RiderManagementList: React.FunctionComponent<
  IRiderManagementListProps
> = (props: IRiderManagementListProps) => {
  const { dummyEnableAddRiderButton } = props;
  let pageScrollPosition: any = useRef(props.currentListPosition);

  const [isSearch, setIsSearching] = useState(
    props.retainedSearchText ? true : false
  );
  const [searchTerm, setSearchTerm] = useState(props.retainedSearchText);
  const [isLoadMore, setLoadMore] = useState(false);
  const [loadMoreText, setLoadMoreText] = useState({
    text: "Load more...",
    isLoading: false,
  });
  const [riderManagementData, setRiderManagementData] = useState<
    Array<IRiderManagementNormalized>
  >(props.testRiderManagementUserStateData || []);

  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  useEffect(() => {
    const {
      getRiderManagementList,
      isRiderListFetched,
      isRiderManagementUserAddUpdate,
    } = props;
    if (isRiderManagementUserAddUpdate || !isRiderListFetched) {
      getRiderManagementList();
      window.scrollTo(0, 0);
    } else {
      window.scrollTo(0, pageScrollPosition.current);
    }
  }, []);

  useEffect(() => {
    const { riders, pagination } = props;
    if (riders) {
      setRiderManagementData(riders);
      setTimeout(() => {
        window.scrollTo(
          0,
          window.pageYOffset - (window.pageYOffset - pageScrollPosition.current)
        );
      }, 250);
      if (pagination) {
        setLoadMore(true);
        setLoadMoreText({
          text: "Load more...",
          isLoading: false,
        });
      } else {
        setLoadMore(false);
      }
    }
  }, [props.riders]);

  useEffect(() => {
    const { error, isError } = props;
    if (isError && error) {
      alert((error! as any).errors);
    }
  }, [props.isError]);

  useEffect(() => {
    if (
      props.isRiderManagementUserAdded ||
      props.isRiderUpdated ||
      props.isRiderCreated
    ) {
      const message =
        props.isRiderManagementUserAdded || props.isRiderCreated
          ? "Rider added successfully!"
          : "Rider updated successfully";
      handleToaster(false, message);
    }
  }, [
    props.isRiderManagementUserAdded,
    props.isRiderUpdated,
    props.isRiderCreated,
  ]);

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  const debounceSearch = useCallback(
    _.debounce(
      (value: any) =>
        doAPIsearch(value, pageScrollPosition, props.getRiderManagementList),
      1000
    ),
    []
  );

  const handleSearchInputChange = (e: any) => {
    const { getRiderManagementList } = props;
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value);
    if (value.length > 0 && !isSearch) {
      setIsSearching(true);
    }
    if (value.length === 0 && isSearch) {
      getRiderManagementList();
    }
  };

  const handleAddUpdatePage = () =>
    props.addUpdatePageHandler(false, null, searchTerm, window.pageYOffset);

  const closeSearch = () => {
    pageScrollPosition.current = 0;
    const { getRiderManagementList } = props;
    getRiderManagementList();
    setIsSearching(false);
    setSearchTerm("");
  };

  const loadFunc = () => {
    if (riderManagementData && !props.isLoading) {
      const { getRiderManagementList, pagination } = props;
      if (pagination) {
        pageScrollPosition.current = window.pageYOffset;
        setLoadMoreText({
          text: "Loading...",
          isLoading: true,
        });
        if (isSearch) {
          getRiderManagementList({
            searchKey: searchTerm,
            page: pagination,
          });
        } else {
          getRiderManagementList({
            page: pagination,
          });
        }
      }
    }
  };

  const editRiderManagementUser = (e: any, index: number) => {
    const { addUpdatePageHandler } = props;
    addUpdatePageHandler(
      true,
      riderManagementData[index],
      searchTerm,
      window.pageYOffset
    );
  };

  const renderRiderManagementList = () => {
    return (riderManagementData! as Array<IRiderManagementNormalized>).map(
      (item, i) => {
        return (
          <tr key={i} onClick={(e) => editRiderManagementUser(e, i)}>
            <td title={item.fullName}>{item.fullName}</td>
            <td title={item.phoneNumber || "-"}>{item.phoneNumber || "-"}</td>
            <td title={item.title || "-"}>{item.title || "-"}</td>
            <td>
              <span
                className={
                  item.status !== STATUS.ACTIVE
                    ? DEACTIVATED_TAG
                    : ACTIVATED_TAG
                }
              >
                {item.status !== STATUS.ACTIVE ? item.status : "active"}
              </span>
            </td>
          </tr>
        );
      }
    );
  };

  return (
    <div className="rider-management-data-table-container">
      <Toaster
        showToaster={showToaster.isVisible}
        toasterClass={
          showToaster.isError
            ? "toaster toaster-bg-danger"
            : "toaster toaster-bg-success"
        }
      >
        {showToaster.message || ""}
      </Toaster>
      <section className="relative container sticky-top">
        <section className="row">
          <section className="col-10">
            <InputGroup>
              <InputGroupAddon addonType="prepend">
                <InputGroupText>
                  <img src={searchicon} alt="close" />
                </InputGroupText>
              </InputGroupAddon>
              <Input
                className="search-input"
                placeholder="Search by phone number"
                onChange={handleSearchInputChange}
                value={searchTerm}
              />
              <div
                className={isSearch ? "close-button" : "hide close-button"}
                onClick={closeSearch}
              >
                <img src={close} alt="close" />
              </div>
            </InputGroup>
          </section>
        </section>
      </section>
      <Can I="read" a="rider">
        <Table
          borderless
          striped
          className="table"
          id="rider-management-user-list"
        >
          <thead>
            <tr className="table-header-row">
              <th>Rider Name</th>
              <th>Phone number</th>
              <th>Title</th>
              <th></th>
            </tr>
          </thead>
          {riderManagementData && <tbody>{renderRiderManagementList()}</tbody>}
          {props.isLoading && <LoaderComponent />}
          {(isSearch &&
            riderManagementData &&
            riderManagementData.length === 0) ||
          dummyEnableAddRiderButton ? (
            <tfoot>
              <tr>
                <div className="no-search-found">
                  {/*<img*/}
                  {/*  src={require("../../../assets/no-search-found.svg")}*/}
                  {/*  alt="no search found"*/}
                  {/*/>*/}
                  <div className="no-search-title">No Rider found</div>
                  <div className="no-search-sub-title">
                    No Rider found on this number, do you want to add a rider?
                  </div>
                  {((searchTerm.length === 11 &&
                    searchTerm.match(CHECK_NUMBER_REGEX)) ||
                    dummyEnableAddRiderButton) && (
                    <div className="mt-4">
                      <Can I="create" a="rider">
                        <ButtonComponent
                          buttonText="Add Rider"
                          onClickHandler={handleAddUpdatePage}
                          buttonClass="btn-primary btn btn-secondary button-large"
                        />
                      </Can>
                    </div>
                  )}
                </div>
              </tr>
            </tfoot>
          ) : (
            ""
          )}
        </Table>
        {isLoadMore && (
          <div id="load-more-container">
            <ButtonComponent
              buttonText={loadMoreText.text}
              buttonClass="btn-primary-outline button-medium"
              onClickHandler={loadFunc}
              isDisable={loadMoreText.isLoading}
            />
          </div>
        )}
      </Can>
    </div>
  );
};

RiderManagementList.defaultProps = {
  dummyEnableAddRiderButton: false,
};

export default RiderManagementList;
